import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppContainerContext } from '../AppContainerContext';
import { ChatMode } from '../chat/types';
import { useAppContainer } from '../useAppContainer';
import { PreviousChatContext } from './DisplacedChatBanner';

type Args = {
  messageCount: number;
  clientId: string | undefined;
  chatMode: ChatMode | undefined;
  entityId: string | undefined;
  turnInFlight: boolean;
  clearMessages: () => void;
  clearUsedActionIds: () => void;
  cancelStreaming: () => void;
  onReturnToConversation: (conversationId: string) => void;
};

export function useDisplacedChat({
  messageCount,
  clientId,
  chatMode,
  entityId,
  turnInFlight,
  clearMessages,
  clearUsedActionIds,
  cancelStreaming,
  onReturnToConversation,
}: Args) {
  const { conversationId, resetConversationId, sidebarContent, skipDefaultSidebar, contentMeta } =
    useAppContainerContext();
  const pageContextMissing = contentMeta === null;
  const { setSiestaMode } = useAppContainer();
  const location = useLocation();
  const navigate = useNavigate();

  const isSiestaSidebarVisible = sidebarContent === null && !skipDefaultSidebar;

  const [previousChatContext, setPreviousChatContext] = useState<PreviousChatContext | null>(null);

  const enteredContextRef = useRef<PreviousChatContext>({
    entityId,
    chatMode,
    clientId,
    url: '',
  });

  const pendingRef = useRef({
    pathname: location.pathname,
    visible: isSiestaSidebarVisible,
    entityId,
    chatMode,
    clientId,
  });

  useEffect(() => {
    // Between one page unmounting and the next declaring its context (a lazily loaded route, a
    // redirect) there is no page context at all. While a turn is in flight that gap is not leaving
    // the chat, or a message sent as a navigation starts would be cancelled by it: the next page's
    // context, or the turn finishing, decides.
    if (pageContextMissing && turnInFlight) return;

    const entered = enteredContextRef.current;
    const pending = pendingRef.current;

    if (
      pending.entityId === entityId &&
      pending.chatMode === chatMode &&
      pending.clientId === clientId &&
      pending.visible &&
      isSiestaSidebarVisible
    ) {
      entered.url = pending.pathname;
    }

    pendingRef.current = {
      pathname: location.pathname,
      visible: isSiestaSidebarVisible,
      entityId,
      chatMode,
      clientId,
    };

    if (
      entered.entityId === entityId &&
      entered.chatMode === chatMode &&
      entered.clientId === clientId
    )
      return;

    const previousUrl = entered.url;
    enteredContextRef.current = { entityId, chatMode, clientId, url: '' };

    if (
      previousChatContext &&
      previousChatContext.entityId === entityId &&
      previousChatContext.chatMode === chatMode &&
      previousChatContext.clientId === clientId
    ) {
      setPreviousChatContext(null);
      onReturnToConversation(conversationId);
      return;
    }

    if (previousChatContext) return;

    if (messageCount > 0) {
      cancelStreaming();
      setPreviousChatContext({
        entityId: entered.entityId,
        chatMode: entered.chatMode,
        clientId: entered.clientId,
        url: previousUrl,
      });
      return;
    }

    clearMessages();
    resetConversationId();
    setSiestaMode('action');
    clearUsedActionIds();
  }, [
    entityId,
    chatMode,
    clientId,
    conversationId,
    location.pathname,
    isSiestaSidebarVisible,
    pageContextMissing,
    turnInFlight,
    previousChatContext,
    messageCount,
    clearMessages,
    clearUsedActionIds,
    resetConversationId,
    setSiestaMode,
    cancelStreaming,
    onReturnToConversation,
  ]);

  const discardChat = (): string => {
    clearMessages();
    const newId = resetConversationId();
    clearUsedActionIds();
    setPreviousChatContext(null);
    return newId;
  };

  // Keep the current conversation (id + messages) and dismiss the displaced banner, so the next
  // message continues the same chat in whatever scope Siesta is now in — used to carry a client
  // chat into the family context.
  const adoptDisplacedChat = () => {
    setPreviousChatContext(null);
  };

  const resetIfDisplaced = (): string => {
    if (!previousChatContext) return conversationId;
    return discardChat();
  };

  const goBack = () => {
    if (previousChatContext?.url) {
      navigate(previousChatContext.url);
    }
  };

  return {
    previousChatContext,
    resetIfDisplaced,
    discardChat,
    adoptDisplacedChat,
    goBack,
  };
}
